import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import Cancel from "@material-ui/icons/Cancel";
import { createCoinData } from "../../store/seller/seller.action";
import * as ActionType from "../../store/seller/seller.type";

function CoinSeller({ createCoinData: sendDiamonds }) {
  const { dialog } = useSelector((state) => state.sellerCoin);
  const { admin } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const lookupVersion = useRef(0);

  const [userId, setUserId] = useState("");
  const [coin, setCoin] = useState("");
  const [user, setUser] = useState(null);
  const [lookupState, setLookupState] = useState("idle");
  const [isSending, setIsSending] = useState(false);

  const resetForm = () => {
    lookupVersion.current += 1;
    setUserId("");
    setCoin("");
    setUser(null);
    setLookupState("idle");
    setIsSending(false);
  };

  const handleClose = () => {
    resetForm();
    dispatch({ type: ActionType.CLOSE_COIN_SELLER_DIALOGUE });
  };

  useEffect(() => {
    if (!dialog) {
      resetForm();
    }
  }, [dialog]);

  useEffect(() => {
    const trimmedUserId = userId.trim();
    const requestVersion = ++lookupVersion.current;

    setUser(null);
    if (!trimmedUserId) {
      setLookupState("idle");
      return undefined;
    }

    setLookupState("loading");
    const lookupTimer = setTimeout(async () => {
      try {
        const response = await axios.get("user/getUserByUniqueId", {
          params: { uniqueId: trimmedUserId },
        });

        if (requestVersion !== lookupVersion.current) return;
        if (response.data.status && response.data.user) {
          setUser(response.data.user);
          setLookupState("found");
        } else {
          setLookupState("notFound");
        }
      } catch (error) {
        if (requestVersion === lookupVersion.current) {
          setLookupState("notFound");
        }
      }
    }, 400);

    return () => clearTimeout(lookupTimer);
  }, [userId]);

  const canSend = Boolean(
    user &&
    lookupState === "found" &&
    Number.isInteger(Number(coin)) &&
    Number(coin) > 0 &&
    !isSending,
  );

  const handleSend = async () => {
    if (!canSend) return;

    setIsSending(true);
    const sent = await sendDiamonds({
      userId: userId.trim(),
      coin: Number(coin),
      coinSellerId: admin._id,
    });
    setIsSending(false);
    if (sent) handleClose();
  };

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || "U";

  return (
    <Dialog
      open={dialog}
      aria-labelledby="send-diamond-title"
      onClose={isSending ? undefined : handleClose}
      fullWidth
      maxWidth="xs"
      style={{ zIndex: 9999999 }}
    >
      <DialogTitle id="send-diamond-title">
        <span className="text-primary font-weight-bold h4 pb-0">
          Send Diamond
        </span>
      </DialogTitle>
      <IconButton
        aria-label="Close"
        disabled={isSending}
        style={{ position: "absolute", right: 0, color: "#664dc9" }}
        onClick={handleClose}
      >
        <Tooltip title="Close" placement="left">
          <Cancel className="cancelButton" />
        </Tooltip>
      </IconButton>

      <DialogContent>
        <div className="modal-body pt-1 px-1 pb-3">
          <div className="form-group mb-0">
            <label
              className="float-left dialog__input__title"
              htmlFor="diamond-user-id"
            >
              User ID
            </label>
            <input
              id="diamond-user-id"
              type="text"
              placeholder="Enter VibeLynk User ID"
              className="form-control form-control-line mt-3 px-2"
              value={userId}
              autoComplete="off"
              onChange={(event) => setUserId(event.target.value)}
            />
            {lookupState === "loading" && (
              <div className="small text-muted mt-2">Looking up user…</div>
            )}
            {lookupState === "notFound" && (
              <div className="small text-danger mt-2">
                No user found for this User ID.
              </div>
            )}
            {user && (
              <RecipientPreview
                user={user}
                userId={userId.trim()}
                userInitial={userInitial}
              />
            )}

            <label
              className="float-left dialog__input__title mt-3"
              htmlFor="diamond-amount"
            >
              Diamonds
            </label>
            <input
              id="diamond-amount"
              type="number"
              min="1"
              step="1"
              placeholder="Diamond amount"
              className="form-control form-control-line mt-3 px-2"
              value={coin}
              onChange={(event) => setCoin(event.target.value)}
            />
            {user && Number(coin) > 0 && (
              <p className=" text-muted mt-3 mb-0" aria-live="polite">
                You are sending{" "}
                <strong className="text-primary">
                  {Number(coin).toLocaleString()} diamonds
                </strong>{" "}
                to{" "}
                <strong className="text-primary">
                  {user.name || "this user"}
                </strong>
                . Confirm details and send.
              </p>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <button
          type="button"
          className="btn btn-secondary px-3"
          disabled={isSending}
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger px-3"
          disabled={!canSend}
          onClick={handleSend}
        >
          {isSending ? "Sending…" : "Send"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

function RecipientPreview({ user, userId, userInitial }) {
  return (
    <div
      className="gap-2 border rounded p-3 mt-3 d-flex align-items-center"
      aria-live="polite"
    >
      {user.image ? (
        <img
          src={user.image}
          alt=""
          width="48"
          height="48"
          className="rounded-circle mr-3"
          style={{ objectFit: "cover" }}
        />
      ) : (
        <span
          className="rounded-circle mr-3 d-inline-flex align-items-center justify-content-center text-white bg-primary"
          style={{ width: 48, height: 48, fontWeight: 700 }}
          aria-hidden="true"
        >
          {userInitial}
        </span>
      )}
      <div className="text-left overflow-hidden">
        <div className="font-weight-bold text-dark text-truncate">
          {user.name || "Unnamed user"}
        </div>
        <div className="small text-muted text-truncate">
          ID: {user.uniqueId || userId}
        </div>
      </div>
    </div>
  );
}

export default connect(null, { createCoinData })(CoinSeller);
